import React, { useState, useEffect, useRef, useCallback } from "react";
import { Modal, Button, Spinner, Alert } from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faQrcode,
  faCheckCircle,
  faCopy,
  faLockOpen,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { postApi } from "../redux/api";
import { LOGIN } from "../redux/actions/actionTypes";
import toast from "react-hot-toast";

function MFASetupModal({ isOpen, onClose }) {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);

  const [show, setShow] = useState(false);
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [codeDigits, setCodeDigits] = useState(["", "", "", "", "", ""]);
  const codeInputRefs = useRef([]);
  const fetchedRef = useRef(false);

  const fetchQrCode = useCallback(async (username) => {
    if (!username) return;
    setLoading(true);
    setError("");
    try {
      const resp = await postApi("mfa/setup/totp", { username });
      if (resp.success) {
        setQrCode(resp.qr_code);
        setSecret(resp.secret);
      } else {
        setError(resp.msg || "Failed to generate QR code");
      }
    } catch (err) {
      setError("Connection error generating 2FA QR code");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen !== undefined) {
      setShow(isOpen);
      if (isOpen && user?.username && !qrCode) {
        fetchQrCode(user.username);
      }
      return;
    }

    // Auto-prompt logic for login: only show once per session if user has NOT enabled 2FA and hasn't skipped
    const skipped = sessionStorage.getItem("mfa_prompt_skipped");
    if (user && user.username && !user.totp_enabled && !skipped && !fetchedRef.current) {
      fetchedRef.current = true;
      setShow(true);
      fetchQrCode(user.username);
    } else if (user && user.totp_enabled) {
      setShow(false);
    }
  }, [user, isOpen, fetchQrCode, qrCode]);

  const handleClose = () => {
    sessionStorage.setItem("mfa_prompt_skipped", "true");
    setShow(false);
    if (onClose) onClose();
  };

  const handleCodeDigitChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newDigits = [...codeDigits];
    newDigits[index] = value;
    setCodeDigits(newDigits);
    if (error) setError("");

    if (value && index < 5 && codeInputRefs.current[index + 1]) {
      codeInputRefs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (index, e) => {
    if (e.key === "Backspace" && !codeDigits[index] && index > 0 && codeInputRefs.current[index - 1]) {
      codeInputRefs.current[index - 1].focus();
    }
    if (e.key === "Enter" && index === 5) {
      handleVerify();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").trim().replace(/\D/g, "").slice(0, 6);
    if (pasteData.length === 6) {
      setCodeDigits(pasteData.split(""));
      if (codeInputRefs.current[5]) codeInputRefs.current[5].focus();
    }
  };

  const handleVerify = async () => {
    const fullCode = codeDigits.join("");
    if (fullCode.length !== 6 || !user?.username) return;

    setVerifying(true);
    setError("");
    try {
      const resp = await postApi("mfa/verify/totp", {
        code: fullCode,
        username: user.username,
        setup: true,
      });

      if (resp.success) {
        dispatch({
          type: LOGIN,
          payload: { ...user, totp_enabled: 1, mfa_enabled: 1 },
        });

        toast.success("Two-Factor Authentication (2FA) enabled successfully!");
        setShow(false);
        if (onClose) onClose();
      } else {
        setError(resp.msg || "Invalid code. Please check your authenticator app and try again.");
        setCodeDigits(["", "", "", "", "", ""]);
        if (codeInputRefs.current[0]) codeInputRefs.current[0].focus();
      }
    } catch {
      setError("Connection error verifying code");
    } finally {
      setVerifying(false);
    }
  };

  const copySecret = () => {
    if (secret) {
      navigator.clipboard.writeText(secret);
      toast.success("Secret key copied to clipboard");
    }
  };

  if (!show) return null;

  return (
    <Modal show={true} onHide={handleClose} centered size="lg">
      <Modal.Header className="bg-light d-flex justify-content-between align-items-center">
        <Modal.Title className="d-flex align-items-center gap-2 mb-0 h6">
          <FontAwesomeIcon icon={faShieldAlt} className="text-primary" />
          Set Up Two-Factor Authentication (2FA)
        </Modal.Title>
        <Button variant="close" aria-label="Close" onClick={handleClose} />
      </Modal.Header>
      <Modal.Body className="p-4">
        <Alert variant="info" className="d-flex align-items-center mb-4">
          <FontAwesomeIcon icon={faLockOpen} className="me-2 fs-5 text-info" />
          <div>
            <strong>Enhance Your Account Security:</strong> Set up Two-Factor Authentication (2FA) using Google Authenticator or Authy. You can also configure this later anytime from your Profile page.
          </div>
        </Alert>

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" className="mb-2" />
            <p className="text-muted small">Generating 2FA QR Code...</p>
          </div>
        ) : (
          <div className="row g-4 align-items-center">
            {/* Step 1: Scan QR Code */}
            <div className="col-md-6 text-center border-end pe-md-4">
              <div className="mb-3">
                <FontAwesomeIcon icon={faQrcode} size="2x" className="text-primary mb-2" />
                <h6 className="fw-bold mb-1">Step 1: Scan QR Code</h6>
                <p className="small text-muted mb-3">
                  Open Google Authenticator, Authy, or Microsoft Authenticator app on your phone and scan:
                </p>
                {qrCode ? (
                  <div className="p-2 bg-white border rounded d-inline-block shadow-sm">
                    <img src={qrCode} alt="2FA QR Code" style={{ width: "170px", height: "170px" }} />
                  </div>
                ) : (
                  <p className="text-danger small">Unable to load QR Code</p>
                )}
              </div>

              {secret && (
                <div className="mt-3 p-2 bg-light rounded text-center">
                  <p className="small text-muted mb-1">Manual Entry Code:</p>
                  <div className="d-flex align-items-center justify-content-center">
                    <code className="me-2 user-select-all small" style={{ letterSpacing: "1px" }}>
                      {secret}
                    </code>
                    <Button variant="link" size="sm" className="p-0 text-primary" onClick={copySecret} title="Copy Secret">
                      <FontAwesomeIcon icon={faCopy} />
                    </Button>
                  </div>
                </div>
              )}
            </div>

            {/* Step 2: Verification Code Input */}
            <div className="col-md-6 ps-md-4">
              <div className="text-center text-md-start mb-3">
                <h6 className="fw-bold mb-1">
                  <FontAwesomeIcon icon={faCheckCircle} className="text-success me-1" />
                  Step 2: Enter 6-Digit Code
                </h6>
                <p className="small text-muted mb-3">
                  Enter the code generated by your authenticator app to confirm setup:
                </p>
              </div>

              <div className="otp-input-group justify-content-center justify-content-md-start mb-3" onPaste={handlePaste}>
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <input
                    key={i}
                    ref={(el) => (codeInputRefs.current[i] = el)}
                    type="text"
                    maxLength={1}
                    className="otp-input me-1"
                    style={{ width: "42px", height: "48px", textAlign: "center", fontSize: "1.2rem", fontWeight: "bold" }}
                    value={codeDigits[i] || ""}
                    onChange={(e) => handleCodeDigitChange(i, e.target.value)}
                    onKeyDown={(e) => handleCodeKeyDown(i, e)}
                    autoFocus={i === 0}
                    autoComplete="one-time-code"
                  />
                ))}
              </div>

              {error && (
                <Alert variant="danger" className="py-2 small mb-3">
                  {error}
                </Alert>
              )}

              <Button
                variant="primary"
                className="w-100 mt-2 py-2 fw-semibold"
                onClick={handleVerify}
                disabled={codeDigits.join("").length !== 6 || verifying}
              >
                {verifying ? (
                  <><Spinner animation="border" size="sm" className="me-1" /> Enabling 2FA...</>
                ) : (
                  <><FontAwesomeIcon icon={faShieldAlt} className="me-1" /> Enable 2FA</>
                )}
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light py-2">
        <Button variant="secondary" size="sm" onClick={handleClose}>
          Skip for Now
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default MFASetupModal;
